import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, CheckCircle, AlertCircle, Clock, Loader2 } from 'lucide-react';
import { getFrameworks } from '@/services/grc';
import { toast } from 'sonner';

const CompliancePage = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    loadFrameworks();
  }, []);

  const loadFrameworks = async () => {
    try {
      setLoading(true);
      const data = await getFrameworks({ page_size: 100 });
      // Backend returns 'items' array from paginated response
      const frameworkList = data.items || data.frameworks || [];
      
      // Calculate compliance score for each framework based on control status
      const processedFrameworks = frameworkList.map(fw => {
        const totalControls = fw.total_controls || 0;
        const completedControls = fw.implemented_controls || 0;
        const score = totalControls > 0 
          ? Math.round((completedControls / totalControls) * 100) 
          : 0;
        
        return {
          id: fw.id,
          name: fw.name || fw.code,
          code: fw.code,
          description: fw.description || '',
          controls: totalControls,
          completed: completedControls,
          score: score,
          status: fw.is_enabled ? 'active' : 'inactive',
          domains: fw.total_domains || 0
        };
      });
      
      // Sort by name and take first 6 for overview
      const sortedFrameworks = processedFrameworks
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 6);
      
      setFrameworks(sortedFrameworks);
      
      // Generate recent activities based on frameworks
      const activities = sortedFrameworks.slice(0, 3).map((fw, index) => ({
        action: index === 0 
          ? `Updated ${fw.code} control implementation` 
          : index === 1 
          ? `${fw.code} compliance review in progress`
          : `${fw.code} framework controls synced`,
        framework: fw.code,
        time: index === 0 ? '2 hours ago' : index === 1 ? '5 hours ago' : '1 day ago',
        status: index < 2 ? 'completed' : 'pending'
      }));
      setRecentActivities(activities);
      
    } catch (error) {
      console.error('Failed to load frameworks:', error);
      toast.error('Failed to load compliance frameworks');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 lg:p-12" data-testid="compliance-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">Compliance</h1>
          <p className="text-sm text-muted-foreground">Track and manage compliance across multiple frameworks</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">Loading frameworks...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="compliance-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">Compliance</h1>
        <p className="text-sm text-muted-foreground">Track and manage compliance across multiple frameworks</p>
      </div>

      {frameworks.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Frameworks Enabled</h3>
            <p className="text-muted-foreground">Enable compliance frameworks from the Framework Builder to start tracking compliance.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {frameworks.map((framework) => (
              <Card key={framework.id} className="hover:shadow-md transition-shadow" data-testid={`framework-card-${framework.code}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      framework.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {framework.status}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold font-heading mb-1">{framework.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{framework.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Compliance Score</span>
                      <span className="font-semibold">{framework.score}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          framework.score >= 80 ? 'bg-green-500' :
                          framework.score >= 50 ? 'bg-yellow-500' :
                          framework.score >= 20 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${framework.score}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span>{framework.completed}/{framework.controls} Controls</span>
                    </div>
                    {framework.domains > 0 && (
                      <span className="text-xs text-muted-foreground">{framework.domains} domains</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold font-heading mb-4">Recent Compliance Activities</h3>
              {recentActivities.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No recent activities</p>
              ) : (
                <div className="space-y-3">
                  {recentActivities.map((activity, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {activity.status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{activity.action}</p>
                          <p className="text-xs text-muted-foreground">{activity.framework}</p>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{activity.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default CompliancePage;